import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Save, Clock, FileText, File, CheckCircle, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

interface NotesTabProps {
  booking: {
    id: string;
    client_id: string;
    practitioner_id: string;
    start_time: string;
    end_time: string;
    status: string;
    format: string;
    clients?: {
      first_name: string;
      last_name: string;
    };
    practitioners?: {
      first_name: string;
      last_name: string;
    };
  };
}

export function NotesTab({ booking }: NotesTabProps) {
  const navigate = useNavigate();
  const [notes, setNotes] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [showSavedMessage, setShowSavedMessage] = useState(false);

  // Auto-save to localStorage
  useEffect(() => {
    const saved = localStorage.getItem(`session-notes-${booking.id}`);
    if (saved) {
      setNotes(saved);
    }
  }, [booking.id]);

  useEffect(() => {
    if (notes) {
      localStorage.setItem(`session-notes-${booking.id}`, notes);
    }
  }, [notes, booking.id]);

  // Warn before page unload if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (notes && !lastSaved) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [notes, lastSaved]);

  const templates = [
    {
      id: 'general',
      name: 'General Session',
      content: `Session Date: ${new Date(booking.start_time).toLocaleDateString()}
Client: ${booking.clients?.first_name} ${booking.clients?.last_name}

Presenting Issues:
- 

Assessment:
- 

Treatment Provided:
- 

Client Response:
- 

Homework/Recommendations:
- 

Next Session Plan:
- 

Additional Notes:
- `
    },
    {
      id: 'initial',
      name: 'Initial Assessment',
      content: `Initial Assessment - ${new Date(booking.start_time).toLocaleDateString()}
Client: ${booking.clients?.first_name} ${booking.clients?.last_name}

Chief Complaint:
- 

History of Present Illness:
- 

Medical History:
- 

Social History:
- 

Mental Status Exam:
- Appearance: 
- Mood: 
- Affect: 
- Speech: 
- Thought Process: 
- Thought Content: 

Assessment & Diagnosis:
- 

Treatment Plan:
- 

Goals:
- Short-term: 
- Long-term: `
    },
    {
      id: 'progress',
      name: 'Progress Note',
      content: `Progress Note - ${new Date(booking.start_time).toLocaleDateString()}
Client: ${booking.clients?.first_name} ${booking.clients?.last_name}

Session Focus:
- 

Progress Since Last Session:
- 

Interventions Used:
- 

Client Response/Engagement:
- 

Barriers to Progress:
- 

Plan for Next Session:
- 

Risk Assessment:
- `
    }
  ];

  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setNotes(template.content);
      setSelectedTemplate(templateId);
    }
  };

  const handleSave = async () => {
    if (!notes.trim()) {
      return;
    }

    setIsSaving(true);

    try {
      // Save to database instead of localStorage
      const { data, error } = await supabase
        .from('medical_notes')
        .insert({
          title: `Session Notes - ${booking.clients?.first_name} ${booking.clients?.last_name} - ${new Date(booking.start_time).toLocaleDateString()}`,
          content: notes,
          note_type: selectedTemplate || 'session',
          practitioner_id: booking.practitioner_id,
          client_id: booking.client_id,
          booking_id: booking.id,
          is_private: false,
          tags: []
        })
        .select()
        .single();

      if (error) throw error;

      setLastSaved(new Date());
      setShowSavedMessage(true);

      // Clear localStorage after successful save
      localStorage.removeItem(`session-notes-${booking.id}`);

      // Hide success message after 10 seconds
      setTimeout(() => {
        setShowSavedMessage(false);
      }, 10000);
    } catch (error) {
      console.error('Error saving session notes:', error);
      // Still update lastSaved but show error
      alert('Failed to save notes to database. Notes are saved locally.');
    } finally {
      setIsSaving(false);
    }
  };

  const formatLastSaved = () => {
    if (!lastSaved) return 'Not saved';
    return `Saved at ${lastSaved.toLocaleTimeString()}`;
  };

  return (
    <div className="space-y-6 pb-4">
      {/* Success Message */}
      {showSavedMessage && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-green-900">Session notes saved successfully!</p>
                <p className="text-sm text-green-700 mt-1">
                  Your notes have been saved and can be viewed in the{' '}
                  <Button
                    variant="link"
                    className="h-auto p-0 text-green-700 underline font-medium"
                    onClick={() => navigate('/practitioner-dashboard?tab=reports')}
                  >
                    Reports section
                    <ExternalLink className="h-3 w-3 ml-1 inline" />
                  </Button>
                  {' '}of your dashboard.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Template Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <File className="h-5 w-5" />
            Note Templates
          </CardTitle>
          <CardDescription>
            Choose a template to get started with structured note-taking
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
            <SelectTrigger>
              <SelectValue placeholder="Select a note template..." />
            </SelectTrigger>
            <SelectContent>
              {templates.map((template) => (
                <SelectItem key={template.id} value={template.id}>
                  {template.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Notes Editor */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Session Notes
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                <Clock className="h-3 w-3 mr-1" />
                {formatLastSaved()}
              </Badge>
              <Button 
                onClick={handleSave}
                disabled={isSaving || !notes.trim()}
                size="sm"
              >
                <Save className="h-4 w-4 mr-1" />
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </CardTitle>
          <CardDescription>
            Document session details, observations, and treatment plans
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Start typing your session notes here..."
            className="min-h-[40vh] sm:min-h-[400px] font-mono text-sm"
          />
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>
            Common note sections and shortcuts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {[
              'Assessment',
              'Treatment Plan',
              'Homework',
              'Next Session',
              'Risk Factors',
              'Progress',
              'Medications',
              'Referrals'
            ].map((section) => (
              <Button
                key={section}
                variant="outline"
                size="sm"
                onClick={() => setNotes(notes + `\n\n${section}:\n- `)}
                className="justify-start text-xs sm:text-sm"
              >
                {section}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}